import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider.js";
import { BrandScreen } from "../../components/shell/BrandScreen.js";
import { readAuthIntent } from "../../auth/authIntent.js";
import { useRobotsMeta } from "../../seo/useRobotsMeta.js";
import { Button, useT } from "@mmm/ui";
interface SignedOutScreenProps {
  mode: "login" | "signedOut";
}

export function SignedOutScreen({ mode }: SignedOutScreenProps) {
  useRobotsMeta("noindex, nofollow");
  const { isReady, signedOut, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useT();
  const label = mode === "signedOut" ? t("auth.signBackIn") : t("auth.signIn");
  const handoff = mode === "login" ? readAuthIntent(searchParams) : null;
  const handoffStarted = useRef(false);
  const [handoffFailed, setHandoffFailed] = useState(false);
  // While a handoff is in flight this screen is a waypoint, not a choice:
  // Entra is already being asked for, and the buttons would only flash as the
  // splash fades and the page leaves. Show what is happening instead — the
  // same line the splash was showing, so the crossfade doesn't change the
  // words. If the redirect fails the buttons come back, because a screen with
  // no way forward is the one thing worse than a flash.
  const handingOff = handoff !== null && !handoffFailed;

  // Arriving from the public site's Sign In / Sign Up button: continue that
  // click here instead of making the visitor press it a second time. Guarded
  // by a ref so a re-render during the MSAL redirect cannot start it twice.
  useEffect(() => {
    if (!handoff || handoffStarted.current || !isReady || !signedOut) return;
    handoffStarted.current = true;
    void (handoff === "signUp" ? signUp() : signIn()).catch(() => setHandoffFailed(true));
  }, [handoff, isReady, signedOut, signIn, signUp]);

  const handleSignIn = async () => {
    await signIn();
    navigate("/home");
  };

  const handleSignUp = async () => {
    await signUp();
    navigate("/home");
  };

  if (isReady && !signedOut) return <Navigate to="/home" replace />;

  return (
    <BrandScreen className="mmm-auth-screen" role="main" aria-label={label}>
      <div className="mmm-brand-cue">
        {handingOff ? (
          <p className="mmm-brand-status">{t("splash.preparingSignIn")}</p>
        ) : mode === "login" ? (
          <div className="mmm-auth-actions" aria-label={t("auth.actions")}>
            <Button variant="primary" onClick={handleSignIn}>
              {t("auth.signIn")}
            </Button>
            <Button variant="secondary" onClick={handleSignUp}>
              {t("auth.signUp")}
            </Button>
          </div>
        ) : (
          <Button variant="primary" onClick={handleSignIn}>
            {label}
          </Button>
        )}
      </div>
    </BrandScreen>
  );
}
